import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import RoleDefaultAccessContent from './components/RoleDefaultAccessContent';
import UserRolesManagementContent from './components/UserRolesManagementContent';
import ReferentDefinitionsContent from './components/ReferentDefinitionsContent';
import InvitationCodesContent from './components/InvitationCodesContent';

/**
 * Page Référent CM : regroupe « Accès par défaut » et « Gérer les rôles »
 * avec le même système d’onglets que la page Configuration.
 */
const CmManagement: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'default-access' | 'roles' | 'referents' | 'invitation-codes'>('default-access');

  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 2;

  useEffect(() => {
    if (!hasAccess) {
      navigate('/panel');
    }
  }, [hasAccess, navigate]);

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                Référent CM
              </h1>
              <p className="text-gray-300">
                Accès par défaut, gestion des rôles utilisateurs et définition des fonctions référent.
              </p>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>

          <Card className="p-6">
            <div className="flex border-b border-gray-700 mb-6">
              <button
                onClick={() => setActiveTab('default-access')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'default-access'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Accès par défaut
              </button>
              <button
                onClick={() => setActiveTab('roles')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'roles'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Gérer les rôles
              </button>
              <button
                onClick={() => setActiveTab('referents')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'referents'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Gestion des référents
              </button>
              <button
                onClick={() => setActiveTab('invitation-codes')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'invitation-codes'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Codes d&apos;invitation
              </button>
            </div>

            <div className="mt-6">
              {activeTab === 'default-access' && <RoleDefaultAccessContent />}
              {activeTab === 'roles' && <UserRolesManagementContent />}
              {activeTab === 'referents' && <ReferentDefinitionsContent />}
              {activeTab === 'invitation-codes' && <InvitationCodesContent />}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CmManagement;
